import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Shield from 'lucide-react/dist/esm/icons/shield';
import Lock from 'lucide-react/dist/esm/icons/lock';
import Eye from 'lucide-react/dist/esm/icons/eye';
import FileText from 'lucide-react/dist/esm/icons/file-text';

const PrivacyPolicyPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
  };

  const sections = [
    { id: 'introduction', title: '1. Introduction' },
    { id: 'definitions', title: '2. Definitions' },
    { id: 'company-info', title: '3. Company Information' },
    { id: 'scope', title: '4. Scope of this Privacy Policy' },
    { id: 'legislative', title: '5. Legislative Framework' },
    { id: 'collection', title: '6. Personal Information We Collect' },
    { id: 'use', title: '7. How We Use Your Personal Information' },
    { id: 'disclosure', title: '8. Disclosure of Your Personal Information' },
    { id: 'transfers', title: '9. International Transfers' },
    { id: 'security', title: '10. Data Security' },
    { id: 'retention', title: '11. Data Retention' },
    { id: 'rights', title: '12. Your Rights' },
    { id: 'changes', title: '13. Changes to Privacy Policy' },
    { id: 'contact', title: '14. Contact Information' },
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <motion.section
        className="bg-gradient-to-br from-[#000565] to-[#0383ED] text-white py-20"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="container-custom">
          <motion.div
            className="flex items-center gap-3 mb-4"
            {...fadeInUp}
          >
            <Lock className="w-8 h-8" />
            <Shield className="w-8 h-8" />
          </motion.div>
          <motion.h1
            className="text-4xl md:text-5xl font-bold mb-4"
            {...fadeInUp}
          >
            Privacy Policy
          </motion.h1>
          <motion.p
            className="text-xl text-blue-100 mb-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Last Updated: 01 November 2025
          </motion.p>
          <motion.p
            className="text-lg text-blue-100 max-w-3xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            Your privacy is important to us. Learn how we collect, use, and protect your personal information.
          </motion.p>
        </div>
      </motion.section>

      {/* Main Content */}
      <div className="container-custom py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Table of Contents - Sticky Sidebar */}
          <motion.aside
            className="lg:w-1/4"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-white rounded-lg shadow-md p-6 lg:sticky lg:top-24">
              <h2 className="text-lg font-bold text-[#000565] mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Table of Contents
              </h2>
              <nav className="space-y-2">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className="block w-full text-left text-sm text-gray-600 hover:text-[#0383ED] hover:pl-2 transition-all duration-200"
                  >
                    {section.title}
                  </button>
                ))}
              </nav>
            </div>
          </motion.aside>

          {/* Content */}
          <motion.main
            className="lg:w-3/4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="bg-white rounded-lg shadow-md p-8 md:p-12">
              {/* Section 1: Introduction */}
              <section id="introduction" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">1. INTRODUCTION</h2>
                <p className="text-gray-700 mb-4">
                  OMC Chartered Accountants (Pty) Ltd (Registration Number 2008/015646/21) ("Company", "we", "us", or "our") is committed to protecting your privacy and ensuring that your personal information is handled in a safe and responsible manner.
                </p>
                <p className="text-gray-700 mb-4">
                  This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you visit our website www.omcca.co.za (the "Website") or use our services. It also describes your rights in relation to your personal information and how to contact us or supervisory authorities in the event you have a complaint.
                </p>
                <p className="text-gray-700">
                  By accessing or using the Website, you acknowledge that you have read and understood this Privacy Policy and consent to the collection, use, and disclosure of your personal information as described herein.
                </p>
              </section>

              {/* Section 2: Definitions */}
              <section id="definitions" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">2. DEFINITIONS</h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    <strong className="text-[#000565]">"Personal Information"</strong> means information relating to an identifiable, living, natural person, and where applicable, an identifiable, existing juristic person, as defined in the Protection of Personal Information Act 4 of 2013 ("POPIA").
                  </p>
                  <p>
                    <strong className="text-[#000565]">"Processing"</strong> means any operation or activity or any set of operations, whether or not by automatic means, concerning personal information, including collection, receipt, recording, organisation, collation, storage, updating, modification, retrieval, alteration, consultation, use, dissemination, merging, linking, restriction, degradation, erasure, or destruction.
                  </p>
                  <p>
                    <strong className="text-[#000565]">"Responsible Party"</strong> means OMC Chartered Accountants (Pty) Ltd, which determines the purpose of and means for processing personal information.
                  </p>
                  <p>
                    <strong className="text-[#000565]">"Data Subject"</strong> means the person to whom personal information relates.
                  </p>
                  <p>
                    <strong className="text-[#000565]">"Operator"</strong> means a person who processes personal information for a responsible party in terms of a contract or mandate, without coming under the direct authority of that party.
                  </p>
                  <p>
                    <strong className="text-[#000565]">"Consent"</strong> means any voluntary, specific, and informed expression of will in terms of which permission is given for the processing of personal information.
                  </p>
                </div>
              </section>

              {/* Section 3: Company Information */}
              <section id="company-info" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">3. COMPANY INFORMATION</h2>
                <p className="text-gray-700 font-semibold mb-3">Responsible Party:</p>
                <div className="bg-gray-50 rounded-lg p-6 space-y-2 text-gray-700">
                  <p className="font-semibold text-[#000565]">OMC Chartered Accountants (Pty) Ltd</p>
                  <p><strong className="text-[#000565]">Registration Number:</strong> 2008/015646/21</p>
                  <p><strong className="text-[#000565]">Registered Address:</strong> 3 North Close, Sunninghill Estate, 2191, South Africa</p>
                  <p><strong className="text-[#000565]">Email Address:</strong> <a href="mailto:admin@omcca.co.za" className="text-[#0383ED] hover:underline">admin@omcca.co.za</a></p>
                  <p><strong className="text-[#000565]">Telephone Number:</strong> <a href="tel:+27100011234" className="text-[#0383ED] hover:underline">+27 10 001 1234</a></p>
                </div>
              </section>

              {/* Section 4: Scope */}
              <section id="scope" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">4. SCOPE OF THIS PRIVACY POLICY</h2>
                <p className="text-gray-700 mb-4">
                  This Privacy Policy applies to personal information processed by the Company in the course of our business, including on our Website, through our services, and through other interactions you may have with us.
                </p>
                <p className="text-gray-700">
                  This Privacy Policy does not apply to information collected by any third party, including through any application or content (including advertising) that may link to or be accessible from the Website. We are not responsible for the privacy practices of such third parties.
                </p>
              </section>

              {/* Section 5: Legislative Framework */}
              <section id="legislative" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">5. LEGISLATIVE FRAMEWORK</h2>
                <p className="text-gray-700 mb-4">
                  Our processing of personal information is governed by the Protection of Personal Information Act 4 of 2013 ("POPIA") and its regulations. We are committed to complying with the eight conditions for the lawful processing of personal information as set out in POPIA:
                </p>
                <ul className="list-none space-y-2 text-gray-700 ml-4">
                  <li>(a) Accountability</li>
                  <li>(b) Processing limitation</li>
                  <li>(c) Purpose specification</li>
                  <li>(d) Further processing limitation</li>
                  <li>(e) Information quality</li>
                  <li>(f) Openness</li>
                  <li>(g) Security safeguards</li>
                  <li>(h) Data subject participation</li>
                </ul>
              </section>

              {/* Section 6: Personal Information We Collect */}
              <section id="collection" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">6. PERSONAL INFORMATION WE COLLECT</h2>

                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">6.1 Information You Provide to Us</h3>
                <p className="text-gray-700 mb-3">
                  We collect personal information that you voluntarily provide to us when you:
                </p>
                <ul className="list-none space-y-2 text-gray-700 ml-4 mb-4">
                  <li>(a) register on the Website;</li>
                  <li>(b) express an interest in obtaining information about us or our services;</li>
                  <li>(c) participate in activities on the Website;</li>
                  <li>(d) request customer support or otherwise communicate with us;</li>
                  <li>(e) engage our professional services;</li>
                  <li>(f) apply for employment with us;</li>
                  <li>(g) subscribe to our newsletters or marketing communications.</li>
                </ul>

                <p className="text-gray-700 mb-3 font-semibold">The personal information we collect may include:</p>
                <div className="space-y-3 text-gray-700 ml-4">
                  <p><strong className="text-[#000565]">Identity Data:</strong> first name, maiden name, last name, username, marital status, title, date of birth, gender, identity number, passport number</p>
                  <p><strong className="text-[#000565]">Contact Data:</strong> postal address, email address, telephone numbers</p>
                  <p><strong className="text-[#000565]">Financial Data:</strong> bank account details, payment card details, tax numbers, financial statements, income information</p>
                  <p><strong className="text-[#000565]">Transaction Data:</strong> details about payments and services purchased</p>
                  <p><strong className="text-[#000565]">Technical Data:</strong> IP address, login data, browser information, device information</p>
                  <p><strong className="text-[#000565]">Profile Data:</strong> username, password, purchases, preferences, feedback</p>
                  <p><strong className="text-[#000565]">Usage Data:</strong> information about how you use our Website and services</p>
                  <p><strong className="text-[#000565]">Marketing Data:</strong> communication preferences</p>
                  <p><strong className="text-[#000565]">Professional Data:</strong> employment history, qualifications, professional memberships</p>
                  <p><strong className="text-[#000565]">Business Data:</strong> company details, registration number, VAT number, director information</p>
                </div>

                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">6.2 Information Collected Automatically</h3>
                <p className="text-gray-700 mb-3">
                  When you visit our Website, we automatically collect certain information about your device, including:
                </p>
                <div className="space-y-3 text-gray-700 ml-4">
                  <p><strong className="text-[#000565]">Log and Usage Data:</strong> service-related diagnostic and performance information, IP address, pages viewed, searches, and actions taken</p>
                  <p><strong className="text-[#000565]">Device Data:</strong> operating system, device identifiers, mobile network information</p>
                  <p><strong className="text-[#000565]">Location Data:</strong> information about device location derived from IP address or device settings</p>
                </div>

                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">6.3 Information from Cookies and Similar Technologies</h3>
                <p className="text-gray-700 mb-4">
                  We use cookies and similar tracking technologies (such as web beacons and pixels) to access or store information. Specific information about how we use such technologies and how you can refuse certain cookies is set out in our Cookie Policy, which forms part of this Privacy Policy.
                </p>
                
                <p className="text-gray-700 font-semibold mb-3">Types of Cookies We Use:</p>
                <div className="space-y-3 text-gray-700 ml-4">
                  <p><strong className="text-[#000565]">Strictly Necessary Cookies:</strong> essential for browsing and using Website features</p>
                  <p><strong className="text-[#000565]">Performance and Functionality Cookies:</strong> enhance Website performance and functionality</p>
                  <p><strong className="text-[#000565]">Analytics and Customisation Cookies:</strong> help understand Website usage and effectiveness of marketing</p>
                  <p><strong className="text-[#000565]">Advertising Cookies:</strong> make advertising messages more relevant to you</p>
                </div>

                <div className="bg-blue-50 border-l-4 border-[#0383ED] p-4 mt-4">
                  <p className="text-gray-700 mb-2">
                    <strong className="text-[#000565]">Managing Cookies:</strong>
                  </p>
                  <p className="text-gray-700">
                    Most web browsers allow control of cookies through browser settings. You can set your browser to refuse all or some cookies, or to alert you when websites set cookies. Note that some Website features may not function properly if cookies are disabled.
                  </p>
                </div>

                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">6.4 Information from Third Parties</h3>
                <p className="text-gray-700 mb-3">
                  We may receive personal information about you from various third parties, including:
                </p>
                <ul className="list-none space-y-2 text-gray-700 ml-4">
                  <li>(a) technical data from analytics providers such as Google;</li>
                  <li>(b) contact, financial, and transaction data from service providers;</li>
                  <li>(c) identity and contact data from publicly available sources;</li>
                  <li>(d) information from credit bureaus and fraud prevention agencies;</li>
                  <li>(e) information from your employer if they engaged our services;</li>
                  <li>(f) information from regulatory bodies and professional associations.</li>
                </ul>

                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">6.5 Special Personal Information</h3>
                <p className="text-gray-700 mb-3">
                  We may collect special personal information (sensitive information) where necessary for service provision, including:
                </p>
                <ul className="list-none space-y-2 text-gray-700 ml-4 mb-4">
                  <li>(a) information about criminal convictions (for due diligence);</li>
                  <li>(b) health information (for insurance or benefits);</li>
                  <li>(c) information about race or ethnic origin (for employment equity reporting).</li>
                </ul>

                <div className="bg-amber-50 border-l-4 border-amber-500 p-4">
                  <p className="text-gray-700 mb-3">
                    <strong className="text-amber-800">Important:</strong> We only process special personal information where you have given explicit consent or another lawful basis exists under POPIA, such as legal obligations or establishment of legal rights.
                  </p>
                </div>

                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">6.6 Children's Personal Information</h3>
                <div className="bg-red-50 border-l-4 border-red-500 p-4">
                  <p className="text-gray-700 mb-2">
                    <strong className="text-red-800">Notice:</strong> Our Website and services are not directed to children under 18.
                  </p>
                  <p className="text-gray-700">
                    We do not knowingly collect personal information from children under 18. If you are under 18, do not use this Website. If we learn we have collected information from a child under 18 without parental consent, we will delete it. Contact us at <a href="mailto:admin@omcca.co.za" className="text-[#0383ED] hover:underline">admin@omcca.co.za</a> if you believe we have information from a child.
                  </p>
                </div>
              </section>

              {/* Section 7: How We Use Your Personal Information */}
              <section id="use" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">7. HOW WE USE YOUR PERSONAL INFORMATION</h2>

                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">7.1 Lawful Bases for Processing</h3>
                <p className="text-gray-700 mb-3">
                  We will only process your personal information where we have a lawful basis. The lawful bases we rely on include:
                </p>
                <ul className="list-none space-y-2 text-gray-700 ml-4">
                  <li><strong className="text-[#000565]">(a) Consent:</strong> you have given clear consent for specific processing</li>
                  <li><strong className="text-[#000565]">(b) Contract:</strong> processing is necessary for our contract with you</li>
                  <li><strong className="text-[#000565]">(c) Legal Obligation:</strong> processing is necessary to comply with the law</li>
                  <li><strong className="text-[#000565]">(d) Legitimate Interests:</strong> processing is necessary for our legitimate interests</li>
                </ul>

                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">7.2 Purposes for Processing</h3>
                <p className="text-gray-700 mb-4">We use your personal information for the following purposes:</p>

                <div className="space-y-4">
                  <div>
                    <p className="font-semibold text-[#000565] mb-2">(a) To provide our services:</p>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                      <li>Preparing financial statements and reports</li>
                      <li>Conducting audits and reviews</li>
                      <li>Providing tax compliance and advisory services</li>
                      <li>Offering business advisory and consulting services</li>
                      <li>Managing client relationships and communications</li>
                    </ul>
                  </div>

                  <div>
                    <p className="font-semibold text-[#000565] mb-2">(b) To manage our contractual relationship:</p>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                      <li>Processing enquiries and requests</li>
                      <li>Creating and managing accounts</li>
                      <li>Processing payments and billing</li>
                      <li>Communicating about services</li>
                      <li>Providing customer support</li>
                    </ul>
                  </div>

                  <div>
                    <p className="font-semibold text-[#000565] mb-2">(c) To comply with legal and regulatory obligations:</p>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                      <li>Complying with POPIA and data protection laws</li>
                      <li>Meeting professional body requirements (SAICA, IRBA)</li>
                      <li>Complying with financial services regulations</li>
                      <li>Anti-money laundering and counter-terrorism compliance</li>
                      <li>Tax law compliance</li>
                      <li>Responding to regulatory and law enforcement requests</li>
                      <li>Maintaining statutory registers and records</li>
                    </ul>
                  </div>

                  <div>
                    <p className="font-semibold text-[#000565] mb-2">(d) To improve our Website and services:</p>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                      <li>Website administration and protection</li>
                      <li>Delivering relevant content and advertisements</li>
                      <li>Data analytics to improve services</li>
                      <li>Making recommendations about services</li>
                    </ul>
                  </div>

                  <div>
                    <p className="font-semibold text-[#000565] mb-2">(e) For marketing purposes:</p>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                      <li>Sending newsletters and promotional materials</li>
                      <li>Conducting market research and surveys</li>
                      <li>Organising events, webinars, and training</li>
                    </ul>
                  </div>

                  <div>
                    <p className="font-semibold text-[#000565] mb-2">(f) For employment purposes:</p>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                      <li>Processing job applications</li>
                      <li>Conducting background checks</li>
                      <li>Managing employee relationships</li>
                      <li>Administering payroll and benefits</li>
                      <li>Employment equity compliance</li>
                    </ul>
                  </div>

                  <div>
                    <p className="font-semibold text-[#000565] mb-2">(g) To protect our business and others:</p>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                      <li>Detecting and preventing fraud</li>
                      <li>Conducting security assessments</li>
                      <li>Protecting safety and security</li>
                      <li>Establishing or defending legal claims</li>
                    </ul>
                  </div>

                  <div>
                    <p className="font-semibold text-[#000565] mb-2">(h) For business transfers:</p>
                    <p className="text-gray-700 ml-4">In connection with mergers, asset sales, financing, or business acquisitions</p>
                  </div>
                </div>

                <h3 className="text-xl font-semibold text-[#000565] mb-3 mt-6">7.3 Marketing Communications</h3>
                <p className="text-gray-700 mb-3">
                  We may use your personal information to send you marketing communications about our services, events, and industry insights. You can opt out of marketing communications at any time by:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Clicking the "unsubscribe" link in any marketing email</li>
                  <li>Contacting us at <a href="mailto:admin@omcca.co.za" className="text-[#0383ED] hover:underline">admin@omcca.co.za</a></li>
                  <li>Updating your communication preferences in your account settings</li>
                </ul>
              </section>

              {/* Section 8: Disclosure */}
              <section id="disclosure" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">8. DISCLOSURE OF YOUR PERSONAL INFORMATION</h2>
                <p className="text-gray-700 mb-4">
                  We may disclose your personal information to third parties in the following circumstances:
                </p>
                <div className="space-y-3 text-gray-700">
                  <p><strong className="text-[#000565]">Service Providers:</strong> IT service providers, cloud hosting providers, payment processors, and other third parties who provide services to us</p>
                  <p><strong className="text-[#000565]">Professional Advisors:</strong> lawyers, auditors, accountants, and other professional advisors</p>
                  <p><strong className="text-[#000565]">Regulatory Authorities:</strong> SARS, IRBA, SAICA, Financial Intelligence Centre, and other regulatory bodies</p>
                  <p><strong className="text-[#000565]">Business Transfers:</strong> potential buyers or investors in connection with business sales or restructuring</p>
                  <p><strong className="text-[#000565]">Legal Requirements:</strong> when required by law, court order, or legal process</p>
                  <p><strong className="text-[#000565]">Protection of Rights:</strong> to protect our rights, property, safety, or that of others</p>
                  <p><strong className="text-[#000565]">With Your Consent:</strong> any other parties with your explicit consent</p>
                </div>
              </section>

              {/* Section 9: International Transfers */}
              <section id="transfers" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">9. INTERNATIONAL TRANSFERS</h2>
                <p className="text-gray-700 mb-4">
                  We may transfer your personal information to countries outside South Africa, including to service providers located internationally. Where we transfer personal information internationally, we ensure appropriate safeguards are in place to protect your information in accordance with POPIA requirements.
                </p>
                <p className="text-gray-700">
                  These safeguards may include using standard contractual clauses, ensuring the recipient country has adequate data protection laws, or obtaining your explicit consent for the transfer.
                </p>
              </section>

              {/* Section 10: Data Security */}
              <section id="security" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">10. DATA SECURITY</h2>
                <p className="text-gray-700 mb-4">
                  We have implemented appropriate technical and organisational security measures to protect your personal information against unauthorised or unlawful processing, accidental loss, destruction, or damage. These measures include:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-4">
                  <li>Encryption of data in transit and at rest</li>
                  <li>Regular security assessments and audits</li>
                  <li>Access controls and authentication mechanisms</li>
                  <li>Employee training on data protection</li>
                  <li>Secure backup and disaster recovery procedures</li>
                  <li>Confidentiality agreements with employees and third parties</li>
                </ul>
                <div className="bg-amber-50 border-l-4 border-amber-500 p-4">
                  <p className="text-gray-700">
                    <strong className="text-amber-800">Please Note:</strong> While we strive to protect your personal information, no method of transmission over the internet or electronic storage is completely secure. We cannot guarantee absolute security of your information.
                  </p>
                </div>
              </section>

              {/* Section 11: Data Retention */}
              <section id="retention" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">11. DATA RETENTION</h2>
                <p className="text-gray-700 mb-4">
                  We will retain your personal information only for as long as necessary to fulfil the purposes for which it was collected, including to satisfy any legal, accounting, or reporting requirements.
                </p>
                <p className="text-gray-700 mb-4">
                  The retention period varies depending on the type of information and the purpose for which it is processed:
                </p>
                <div className="space-y-3 text-gray-700">
                  <p><strong className="text-[#000565]">Client Records:</strong> retained for at least 5 years after the end of the professional relationship or as required by professional body rules</p>
                  <p><strong className="text-[#000565]">Financial Records:</strong> retained for at least 5 years as required by tax legislation</p>
                  <p><strong className="text-[#000565]">Audit Files:</strong> retained for at least 5 years or as required by IRBA regulations</p>
                  <p><strong className="text-[#000565]">Employment Records:</strong> retained for at least 3-5 years after employment ends</p>
                  <p><strong className="text-[#000565]">Marketing Data:</strong> retained until you opt out or request deletion</p>
                </div>
                <p className="text-gray-700 mt-4">
                  When we no longer need your personal information, we will securely delete or anonymise it in accordance with our data retention and disposal policies.
                </p>
              </section>

              {/* Section 12: Your Rights */}
              <section id="rights" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">12. YOUR RIGHTS</h2>
                <p className="text-gray-700 mb-4">
                  Under POPIA, you have the following rights regarding your personal information:
                </p>

                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-[#000565] mb-2">Right of Access</h4>
                    <p className="text-gray-700">
                      You have the right to request access to the personal information we hold about you and to request a copy of your personal information.
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-[#000565] mb-2">Right to Correction</h4>
                    <p className="text-gray-700">
                      You have the right to request that we correct or update any inaccurate or incomplete personal information we hold about you.
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-[#000565] mb-2">Right to Deletion</h4>
                    <p className="text-gray-700">
                      You have the right to request that we delete your personal information in certain circumstances, such as when it is no longer necessary for the purpose for which it was collected, or if you withdraw your consent.
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-[#000565] mb-2">Right to Object</h4>
                    <p className="text-gray-700">
                      You have the right to object to the processing of your personal information for direct marketing purposes or where we are relying on legitimate interests as the basis for processing.
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-[#000565] mb-2">Right to Restriction</h4>
                    <p className="text-gray-700">
                      You have the right to request that we restrict the processing of your personal information in certain circumstances, such as when you contest its accuracy or object to processing.
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-[#000565] mb-2">Right to Data Portability</h4>
                    <p className="text-gray-700">
                      You have the right to receive your personal information in a structured, commonly used, and machine-readable format and to transmit that information to another party where technically feasible.
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-[#000565] mb-2">Right to Withdraw Consent</h4>
                    <p className="text-gray-700">
                      Where we are processing your personal information based on consent, you have the right to withdraw your consent at any time. This will not affect the lawfulness of processing based on consent before its withdrawal.
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-[#000565] mb-2">Right to Lodge a Complaint</h4>
                    <p className="text-gray-700 mb-2">
                      You have the right to lodge a complaint with the Information Regulator if you believe we have not complied with POPIA. The Information Regulator can be contacted at:
                    </p>
                    <div className="text-gray-700 ml-4">
                      <p>Information Regulator (South Africa)</p>
                      <p>JD House, 27 Stiemens Street</p>
                      <p>Braamfontein, Johannesburg, 2001</p>
                      <p>Email: inforeg@justice.gov.za</p>
                      <p>Website: www.justice.gov.za/inforeg</p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border-l-4 border-[#0383ED] p-4 mt-6">
                  <p className="text-gray-700 mb-2">
                    <strong className="text-[#000565]">To Exercise Your Rights:</strong>
                  </p>
                  <p className="text-gray-700">
                    To exercise any of these rights, please contact us at <a href="mailto:admin@omcca.co.za" className="text-[#0383ED] hover:underline font-medium">admin@omcca.co.za</a> or using the contact details in Section 14. We will respond to your request within a reasonable timeframe and in accordance with applicable law.
                  </p>
                </div>

                <p className="text-gray-700 mt-4">
                  Please note that we may need to verify your identity before processing your request. We may also need to retain certain information for legal or legitimate business purposes.
                </p>
              </section>

              {/* Section 13: Changes to Privacy Policy */}
              <section id="changes" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">13. CHANGES TO THIS PRIVACY POLICY</h2>
                <p className="text-gray-700 mb-4">
                  We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. When we make changes, we will update the "Last Updated" date at the top of this Privacy Policy.
                </p>
                <p className="text-gray-700 mb-4">
                  We encourage you to review this Privacy Policy periodically to stay informed about how we are protecting your personal information.
                </p>
                <div className="bg-blue-50 border-l-4 border-[#0383ED] p-4">
                  <p className="text-gray-700">
                    <strong className="text-[#000565]">Material Changes:</strong> If we make material changes to this Privacy Policy that significantly affect your rights, we will notify you by email (if you have provided your email address) or by posting a prominent notice on our Website before the changes take effect.
                  </p>
                </div>
              </section>

              {/* Section 14: Contact Information */}
              <section id="contact" className="mb-12 scroll-mt-24">
                <h2 className="text-2xl font-bold text-[#000565] mb-4">14. CONTACT INFORMATION</h2>
                <p className="text-gray-700 mb-4">
                  If you have any questions, concerns, or complaints about this Privacy Policy or our data practices, or if you wish to exercise any of your rights under POPIA, please contact us at:
                </p>
                <div className="bg-gradient-to-br from-[#000565] to-[#0383ED] text-white rounded-lg p-6 mb-6">
                  <p className="font-semibold mb-3">OMC Chartered Accountants (Pty) Ltd</p>
                  <p className="mb-1">Attention: Information Officer</p>
                  <p className="mb-1">3 North Close</p>
                  <p className="mb-1">Sunninghill Estate</p>
                  <p className="mb-4">2191, South Africa</p>
                  <p className="mb-1">Email: <a href="mailto:admin@omcca.co.za" className="text-white hover:text-blue-200 underline">admin@omcca.co.za</a></p>
                  <p>Telephone: <a href="tel:+27100011234" className="text-white hover:text-blue-200 underline">+27 10 001 1234</a></p>
                </div>

                <p className="text-gray-700 mb-4">
                  We will investigate and attempt to resolve complaints and disputes regarding the use and disclosure of personal information in accordance with the principles contained in this Privacy Policy.
                </p>

                <p className="text-gray-700">
                  We aim to respond to all privacy-related enquiries and complaints within 30 days. If you are not satisfied with our response, you have the right to lodge a complaint with the Information Regulator as outlined in Section 12.
                </p>
              </section>

              {/* Bottom Notice */}
              <div className="mt-12 pt-8 border-t border-gray-200">
                <div className="flex items-start gap-3 bg-green-50 rounded-lg p-6">
                  <Shield className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-gray-700 mb-2">
                      <strong className="text-green-800">Your Privacy Matters:</strong> We are committed to protecting your personal information and respecting your privacy rights. By using our Website and services, you acknowledge that you have read and understood this Privacy Policy.
                    </p>
                    <p className="text-gray-700">
                      For more information about our legal terms, please review our <Link to="/terms-of-use" className="text-[#0383ED] hover:underline font-medium">Terms of Use</Link>. If you have questions, please <Link to="/contact" className="text-[#0383ED] hover:underline font-medium">contact us</Link>.
                    </p>
                  </div>
                </div>
              </div>

              {/* POPIA Compliance Badge */}
              <div className="mt-6 flex items-center justify-center gap-2 bg-gray-50 rounded-lg p-4">
                <Eye className="w-5 h-5 text-[#000565]" />
                <p className="text-sm text-gray-600">
                  This Privacy Policy complies with the Protection of Personal Information Act (POPIA) 4 of 2013
                </p>
              </div>
            </div>
          </motion.main>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;